/**
 * 🎁 NestBoxDrawer — Pick-1-of-5 reward modal that fires after boss
 * defeats (Dream Forest solo + MPD wins). Owner canon Mar 5, 2026.
 *
 * Behavior:
 *   • On mount, polls `/api/nest-box/pending`. If a pending box exists,
 *     opens the drawer. Otherwise stays hidden.
 *   • User taps one of the 5 tiles → POST /api/nest-box/pick → toast +
 *     dismiss + re-poll (for stacked drops).
 *
 * Placement: mount once, high in the app tree. It self-manages visibility.
 */
import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function NestBoxDrawer() {
  const [box, setBox] = useState(null);
  const [picking, setPicking] = useState(null);
  const [reveal, setReveal] = useState(null);   // shown briefly on pick

  const poll = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/nest-box/pending`);
      const first = (r.data?.boxes || []).find((b) => !b.picked_index && b.picked_index !== 0);
      // Note: picked_index is null on pending boxes; filter above just
      // guards against future shape drift.
      const next = (r.data?.boxes || [])[0];
      if (next && next.box_id !== box?.box_id) setBox(next);
      else if (!next) setBox(null);
    } catch (e) {
      // silent — the drawer is opportunistic
    }
  }, [box]);

  useEffect(() => {
    poll();
    const iv = setInterval(poll, 25000);   // ambient re-poll every 25s
    return () => clearInterval(iv);
  }, [poll]);

  // Also expose an imperative refresh so other components can prod the
  // drawer immediately after a boss-defeat call. Uses a window event to
  // avoid a global store dependency.
  useEffect(() => {
    const handler = () => poll();
    window.addEventListener("nest-box:refresh", handler);
    return () => window.removeEventListener("nest-box:refresh", handler);
  }, [poll]);

  const onPick = async (idx) => {
    if (!box || picking !== null) return;
    setPicking(idx);
    try {
      const r = await axios.post(`${API}/nest-box/pick`, {
        box_id: box.box_id,
        option_index: idx,
      });
      const chosen = r.data?.picked;
      setReveal(chosen);
      toast.success(`${chosen?.icon || "🎁"} ${chosen?.label} claimed!`);
      // Small dramatic pause so the user sees the reveal
      setTimeout(() => {
        setBox(null);
        setReveal(null);
        setPicking(null);
        poll();   // pick up stacked drops
      }, 1400);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't claim that pick.");
      setPicking(null);
    }
  };

  if (!box) return null;
  const sourceLabel = ((box.source || "")
    .replace("dream_forest_boss_kill", "Dream Forest Boss")
    .replace(/^mpd_win:.*/, "MPD Victory"));

  return (
    <div
      className="fixed inset-0 z-[95] bg-black/90 flex items-center justify-center p-4"
      data-testid="nest-box-drawer"
      role="dialog"
    >
      <div
        className="w-full max-w-lg rounded-2xl border-2 border-amber-500/60 bg-gradient-to-br from-black via-amber-950/25 to-black p-6 relative overflow-hidden"
        style={{ boxShadow: "0 0 40px rgba(217, 119, 6, 0.35)" }}
      >
        {/* Accent bar */}
        <div className="absolute inset-x-0 top-0 h-[3px] bg-gradient-to-r from-amber-600 via-yellow-400 to-amber-600" />

        <div className="text-center mb-4">
          <div className="text-[10px] uppercase tracking-[0.4em] text-amber-400 mb-1">
            🎁 Nest Reward
          </div>
          <h3
            className="text-3xl font-black text-amber-100"
            style={{ fontFamily: "Anton, sans-serif" }}
          >
            PICK ONE
          </h3>
          <p className="text-[11px] text-amber-100/60 italic mt-1">
            Awarded for defeating a boss — {sourceLabel}. Choose one. The other four dissolve.
          </p>
        </div>

        {reveal ? (
          <div className="py-10 text-center animate-pulse" data-testid="nest-box-reveal">
            <div className="text-6xl mb-3">{reveal.icon}</div>
            <div className="text-xl font-black text-amber-200">{reveal.label}</div>
            <div className="text-xs text-amber-100/70 mt-1">{reveal.detail}</div>
          </div>
        ) : (
          <div className="grid grid-cols-5 gap-2.5" data-testid="nest-box-facedown-grid">
            {(box.options || []).map((opt, idx) => {
              const isPicking = picking === idx;
              const isDim = picking !== null && picking !== idx;
              return (
                <button
                  key={idx}
                  type="button"
                  onClick={() => onPick(idx)}
                  disabled={picking !== null}
                  data-testid={`nest-box-option-${idx}`}
                  aria-label={`Mystery reward ${idx + 1}`}
                  className={`aspect-[3/4] rounded-lg border-2 flex flex-col items-center justify-center transition-all relative overflow-hidden ${
                    isDim
                      ? "opacity-20 border-neutral-800 bg-black/60"
                      : "border-amber-700/60 bg-gradient-to-br from-amber-950/40 via-black to-purple-950/40 hover:border-amber-400 hover:scale-[1.03]"
                  } ${isPicking ? "border-amber-300 ring-2 ring-amber-400/70 scale-[1.05]" : ""}`}
                >
                  {/* Face-down card pattern */}
                  <div
                    className="absolute inset-0 opacity-30"
                    style={{
                      backgroundImage:
                        "radial-gradient(circle at 30% 30%, rgba(245,158,11,0.4) 0, transparent 40%), radial-gradient(circle at 70% 70%, rgba(139,92,246,0.35) 0, transparent 40%)",
                    }}
                  />
                  <div className="relative text-4xl text-amber-300 drop-shadow-[0_0_8px_rgba(245,158,11,0.6)]">
                    ❓
                  </div>
                  <div className="relative text-[9px] uppercase tracking-widest text-amber-200/70 mt-1 font-bold">
                    Mystery
                  </div>
                </button>
              );
            })}
          </div>
        )}

        <div className="mt-4 text-center text-[10px] text-amber-100/40 italic">
          🐦‍🔥 The phoenix chooses. Pick with intention.
        </div>
      </div>
    </div>
  );
}
